import * as Yup from "yup"

export const customerValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .required(t("global.name_is_required", "Name is required"))
      .min(2, t("global.name_must_be_atleast_two_characters", "Name needs to be at least 2 characters long"))
      .nullable(),
    organisationNumber: Yup.number()
      .nullable()
      .typeError(t("organization_number_must_be_a_number", "Organization number needs to be a number")),
    streetAddress: Yup.string().nullable(),
    postalCode: Yup.number()
      .nullable()
      .typeError(t("global.postnumber_number_must_be_a_number", "Postal code needs to be a number")),
    cityAddress: Yup.string().nullable(),
    newsMaxUsers: Yup.number()
      .nullable()
      .typeError(
        t("settings.user.number_of_licenses_must_be_a_number", "Number of licenses must be a number")
      ),
    learningMaxUsers: Yup.number()
      .nullable()
      .typeError(
        t("settings.user.number_of_licenses_must_be_a_number", "Number of licenses must be a number")
      ),
    customerAdminMaxUsers: Yup.number()
      .nullable()
      .typeError(
        t("settings.user.number_of_licenses_must_be_a_number", "Number of licenses must be a number")
      ),
    systemAdminCustomersMaxUsers: Yup.number()
      .nullable()
      .typeError(
        t("settings.user.number_of_licenses_must_be_a_number", "Number of licenses must be a number")
      ),
    systemAdminDeviceRegistryMaxUsers: Yup.number()
      .nullable()
      .typeError(
        t("settings.user.number_of_licenses_must_be_a_number", "Number of licenses must be a number")
      ),
    bplDaysShown: Yup.number()
      .nullable()
      .typeError(t("settings.user.number_of_days_must_be_a_number", "Number of days must be a number"))
  })

export const companyValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .required(t("global.name_is_required", "Name is required"))
      .min(2, t("global.name_must_be_atleast_two_characters", "Name needs to be at least 2 characters long")),
    organisationNumber: Yup.number().typeError(
      t("organization_number_must_be_a_number", "Organization number needs to be a number")
    ),
    streetAddress: Yup.string(),
    postalCode: Yup.string(),
    cityAddress: Yup.string()
  })

export const departmentValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .required(t("global.name_is_required", "Name is required"))
      .min(2, t("global.name_must_be_atleast_two_characters", "Name needs to be at least 2 characters long")),
    description: Yup.string()
  })

export const userValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().nullable().required(t("global.name_is_required", "Name is required")),
    username: Yup.string()
      .nullable()
      .required(t("global.username_is_required", "Username is required"))
      .matches(/^(\S+$)/g, t("global.field_cannot_contain_spaces", "Field cannot contain spaces"))
      .when("userExists", {
        is: (value) => value === true,
        then: (schema) =>
          schema.matches(true, t("global.organization_number_is_required", "Organization number is required"))
      }),
    email: Yup.string()
      .nullable()
      .email(t("global.email_is_malformed", "Email is malformed"))
      .when("emailTaken", {
        is: (value) => value === true,
        then: (schema) =>
          schema.test(
            "email-taken",
            t("global.the_email_address_is_already_taken", "The email address is already taken"),
            () => false
          )
      }),
    bplUserId: Yup.number().nullable(true),
    userExists: Yup.boolean(),
    useUsername: Yup.boolean(),
    emailTaken: Yup.boolean(),
    employeeId: Yup.string().nullable(true),
    mainCompanyId: Yup.number()
      .nullable(true)
      .test(
        "mainCompanyId",
        t("settings.user.company_is_not_linked_to_the_user", "Company is not linked to the user"),
        function (value, yupObj) {
          if (value !== null && value !== undefined) {
            return yupObj?.parent?.companyIds?.includes(value)
          }
          return true
        }
      ),
    isManager: Yup.boolean(),
    streetAddress: Yup.string()
      .nullable()
      .max(150, t("global.cannot_be_longer_than_150_characters", "Cannot be longer than 150 characters")),
    postalCode: Yup.string()
      .nullable()
      .max(6, t("global.cannot_be_longer_than_6_characters", "Cannot be longer than 6 characters")),
    cityAddress: Yup.string()
      .nullable()
      .max(150, t("global.cannot_be_longer_than_150_characters", "Cannot be longer than 150 characters"))
  })

export const userCreateValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().nullable().required(t("global.name_is_required", "Name is required")),
    username: Yup.string()
      .nullable()
      .when("useUsername", {
        is: (value) => value === false,
        then: (schema) => schema.email(t("global.email_is_malformed", "Email is malformed"))
      })
      .when("userExists", {
        is: (value) => value === true,
        then: (schema) =>
          schema.matches(true, t("global.organization_number_is_required", "Organization number is required"))
      })
      .required(t("global.username_is_required", "Username is required"))
      .matches(/^(\S+$)/g, t("global.field_cannot_contain_spaces", "Field cannot contain spaces")),
    email: Yup.string()
      .nullable()
      .email(t("global.email_is_malformed", "Email is malformed"))
      .when("emailTaken", {
        is: (value) => value === true,
        then: (schema) =>
          schema.test(
            "email-taken",
            t("global.the_email_address_is_already_taken", "The email address is already taken"),
            () => false
          )
      }),
    password: Yup.string().nullable().required(t("global.password_is_required", "Password is required")),
    sendEmailOnCreate: Yup.boolean(),
    userExists: Yup.boolean(),
    emailTaken: Yup.boolean(),
    useUsername: Yup.boolean(),
    mainCompanyId: Yup.number()
      .nullable(true)
      .test(
        "mainCompanyId",
        t("settings.user.company_is_not_linked_to_the_user", "Company is not linked to the user"),
        function (value, yupObj) {
          if (value !== null && value !== undefined) {
            return yupObj?.parent?.companyIds?.includes(value)
          }
          return true
        }
      ),
    isManager: Yup.boolean(),
    streetAddress: Yup.string()
      .nullable()
      .max(150, t("global.cannot_be_longer_than_150_characters", "Cannot be longer than 150 characters")),
    postalCode: Yup.string()
      .nullable()
      .max(6, t("global.cannot_be_longer_than_6_characters", "Cannot be longer than 6 characters")),
    cityAddress: Yup.string()
      .nullable()
      .max(150, t("global.cannot_be_longer_than_150_characters", "Cannot be longer than 150 characters"))
  })
